import type { MaskedTile as Tile } from '@completto/shared';
import { rotationCandidates } from '@completto/shared';
import { html, View } from 'rune-ts';

export type HoldingCardData = {
  tile: Tile | null;
  origin: 'DECK' | 'POOL' | null; // DECK이면 회전 미결정, POOL이면 결정됨
  showFlowHint: boolean;
  /**
   * true면 PC에서도 모바일 가로처럼 가운데에 크게 표시
   * (column flex + 큰 타일 + 큰 일러스트 + 부제).
   * mlandscape:는 항상 prominent와 동일한 스타일.
   */
  prominent?: boolean;
  /**
   * true면 lane 헤더 옆 인접 badge 형태로 압축 표시.
   * 라벨/힌트/원본 안내를 모두 숨기고 타일 박스만 작게 노출 (40×52).
   * compact 우선순위는 prominent보다 높음.
   */
  compact?: boolean;
  /** prominent 모드에서 부제 아래에 inline ghost 버튼으로 노출. 없으면 hidden. */
  onDiscard?: () => void;
  /** onDiscard와 함께 사용. false면 ghost 버튼이 disabled로 보임. */
  discardEnabled?: boolean;
};

export class HoldingCardView extends View<HoldingCardData> {
  override template() {
    const { tile, origin, showFlowHint, prominent = false, compact = false } = this.data;
    const hasTile = tile !== null && tile.number !== null;

    if (compact) {
      const display = hasTile && tile ? (tile.resolvedNumber ?? tile.number) : null;
      const tileCls = hasTile
        ? 'flex items-center justify-center w-10 h-12 rounded-md bg-card-navy text-card-gold text-lg font-extrabold shadow-sm shrink-0'
        : 'flex items-center justify-center w-10 h-12 rounded-md bg-wood-paper/40 ring-1 ring-dashed ring-wood-sand text-caption text-wood-coffee/60 shrink-0';
      return html`
        <div class="holding-card-compact" aria-label="${hasTile ? `손에 든 타일 ${display}` : '손에 든 타일 없음'}">
          <div class="${tileCls}">${hasTile ? display : '·'}</div>
        </div>
      `;
    }
    const layoutCls = prominent
      ? 'flex-col items-center justify-center gap-2 p-4 min-h-[200px]'
      : 'flex items-center gap-3 p-3 min-h-[88px] mlandscape:flex-col mlandscape:gap-2 mlandscape:p-3';
    const skinCls = hasTile
      ? 'bg-card-gold/15 ring-2 ring-card-gold animate-pulse-slow'
      : 'bg-wood-paper/40 ring-1 ring-dashed ring-wood-sand';
    const containerCls = `holding-card flex rounded-xl ${layoutCls} ${skinCls}`;

    if (!hasTile || !tile) {
      const subCls = prominent
        ? 'text-caption text-wood-coffee/60 mt-0.5'
        : 'hidden mlandscape:block text-caption text-wood-coffee/60 mt-0.5';
      const textCls = prominent
        ? 'text-subhead text-wood-coffee/75 text-center'
        : 'text-body text-wood-coffee/60 mlandscape:text-subhead mlandscape:text-wood-coffee/75 text-center';
      return html`
        <div class="${containerCls}">
          <div class="${textCls}">
            <div>${'손에 든 타일 없음'}</div>
            <div class="${subCls}">
              더미·풀에서 가져오세요
            </div>
          </div>
        </div>
      `;
    }

    const display = tile.resolvedNumber ?? tile.number;
    const candidates = rotationCandidates(tile.number ?? 0);
    const rotatable = candidates.length > 1 && tile.resolvedNumber === null;
    const originLabel = origin === 'POOL' ? '풀에서 가져옴' : '더미에서 뽑음';
    const altText =
      rotatable && candidates[1] !== undefined ? `${candidates[0]} 또는 ${candidates[1]}` : null;

    const tileBoxCls = prominent
      ? 'holding-tile-enter flex items-center justify-center w-20 h-24 rounded-lg bg-card-navy text-card-gold text-display font-extrabold shadow-md shrink-0'
      : 'holding-tile-enter flex items-center justify-center w-14 h-16 mlandscape:w-20 mlandscape:h-24 rounded-lg bg-card-navy text-card-gold text-3xl mlandscape:text-display font-extrabold shadow-md shrink-0';
    const metaCls = prominent
      ? 'flex flex-col gap-0.5 items-center text-center'
      : 'flex flex-col gap-0.5 grow min-w-0 mlandscape:items-center mlandscape:text-center mlandscape:grow-0';
    const originCls = prominent
      ? 'text-caption text-wood-coffee'
      : 'text-body mlandscape:text-caption text-wood-coffee';

    const showDiscardInline = prominent && this.data.onDiscard !== undefined;
    const discardEnabled = this.data.discardEnabled !== false;
    const discardCls =
      'bg-wood-paper hover:bg-wood-cream text-wood-coffee ring-1 ring-wood-sand shadow-sm hover:shadow active:scale-[0.98] cursor-pointer';
    return html`
      <div class="${containerCls}">
        <div class="${tileBoxCls}">
          ${display}
        </div>
        <div class="${metaCls}">
          <div class="text-caption text-card-brass font-semibold">손에 든 타일</div>
          <div class="${originCls}">
            ${originLabel}
          </div>
          ${
            altText
              ? html`<div class="text-caption text-wood-coffee/70"><span class="text-lg align-middle">⟲</span> 회전 가능: ${altText}</div>`
              : html``
          }
          ${
            showDiscardInline && discardEnabled
              ? html`<button
                  type="button"
                  data-role="discard"
                  class="mlandscape:hidden inline-flex items-center gap-1.5 mt-2 rounded-lg px-3 py-1.5 text-caption font-semibold transition ${discardCls}"
                  aria-label="현재 손에 든 타일을 풀로 버리기"
                >
                  <span aria-hidden="true">🗑</span>
                  <span>풀로 버리기</span>
                </button>`
              : html``
          }
          ${
            showDiscardInline && !discardEnabled
              ? html`<div class="mt-2 text-caption text-wood-coffee/60 mlandscape:text-wood-coffee/55">
                  풀 타일은 lane에 배치하세요
                </div>`
              : html``
          }
        </div>
        ${
          showFlowHint && !prominent
            ? html`<div
                class="flow-hint pointer-events-none flex items-center justify-center text-card-gold text-xl mt-1 animate-bounce mlandscape:hidden"
                aria-hidden="true"
              >
                ▼
              </div>`
            : html``
        }
      </div>
    `;
  }

  protected override onMount(): void {
    const root = this.element();
    const discardBtn = root.querySelector<HTMLButtonElement>('[data-role="discard"]');
    discardBtn?.addEventListener('click', () => {
      const onDiscard = this.data.onDiscard;
      if (!onDiscard) return;
      if (this.data.discardEnabled === false) return;
      onDiscard();
    });
  }
}
